import { useState, useRef, useEffect } from "react";
import type {
  InvestorProfile,
  HouseholdMember,
  PersonRelationship,
  ReducedIncomePeriod,
  VerificationDocument,
  TaxDocument,
  PayslipEntry,
  Structure,
  TeamContact,
  TeamContactRole,
} from "../../types/entities";
import type { PropertyRecord } from "../../types/portfolio";
import { calculateEffectiveRate, calculateMarginalRate } from "../../lib/calculations";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";
import { migrateProfileDocs } from "../../lib/docMigration";
import { uploadToStorage as sharedUploadToStorage, downloadFromStorage as sharedDownloadFromStorage, deleteFromStorage as sharedDeleteFromStorage } from "../../lib/userStorage";
import StructureModal from "../portfolio/StructureModal";
import { NumberInput } from "../ui/NumberInput";
import { TabExplainer } from "../ui/TabExplainer";
import { InfoTooltip } from "../ui/InfoTooltip";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function currency(n: number) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);
}

// ─── Small UI primitives ──────────────────────────────────────────────────────

function EditField({
  label,
  value,
  onChange,
  onBlur,
  type = "text",
  placeholder,
  helper,
}: {
  label: string;
  value: string | number | undefined;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  helper?: string;
}) {
  const displayValue = type === "number" && (value === 0 || value === "0") ? "" : (value ?? "");
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-neutral-500">{label}</label>
      {type === "number" ? (
        <NumberInput
          className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
          value={displayValue}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : (
        <input
          type={type}
          className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
          value={displayValue}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
      {helper && <p className="text-xs text-neutral-400">{helper}</p>}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-neutral-500">{label}</label>
      <select
        className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── CollapsibleSection ───────────────────────────────────────────────────────

function CollapsibleSection({
  id: _id,
  title,
  summary,
  collapsed,
  onToggleCollapse,
  children,
}: {
  id: string;
  title: string;
  summary: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-neutral-100 bg-neutral-50">
      <div
        className="flex cursor-pointer items-center justify-between px-5 py-4"
        onClick={onToggleCollapse}
      >
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest text-neutral-400">{title}</div>
          {collapsed && <div className="mt-0.5 truncate text-sm text-neutral-600">{summary}</div>}
        </div>
        <span className="ml-3 shrink-0 text-neutral-400">{collapsed ? "▼" : "▲"}</span>
      </div>
      {!collapsed && <div className="border-t border-neutral-100 px-5 pb-5 pt-4">{children}</div>}
    </div>
  );
}

// ─── Doc upload slot ──────────────────────────────────────────────────────────

function DocUploadSlot({
  label,
  doc,
  onUploadFile,
  onDownload,
  onClearFile,
  onRemoveDoc,
  removable,
  uploading,
}: {
  label: string;
  doc?: VerificationDocument;
  onUploadFile: (docId: string, label: string, file: File) => void;
  onDownload?: (filePath: string) => void;
  onClearFile?: (docId: string) => void;
  onRemoveDoc?: (docId: string) => void;
  removable?: boolean;
  uploading?: boolean;
}) {
  const hasFile = !!doc?.filename;
  const needsReupload = doc?.needsReupload && !doc?.filePath;
  return (
    <div className={`flex items-center justify-between rounded-xl border bg-white px-4 py-3 ${needsReupload ? "border-amber-300 bg-amber-50" : "border-neutral-100"}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-neutral-700">{label}</div>
        {hasFile && (
          <div className="text-xs text-neutral-400 mt-0.5 truncate flex items-center gap-1.5">
            {doc.filePath && onDownload ? (
              <button onClick={() => onDownload(doc.filePath!)} className="text-blue-600 hover:underline">{doc.filename}</button>
            ) : (
              <span>{doc.filename}</span>
            )}
            {doc.dateUploaded && <span>· {doc.dateUploaded}</span>}
          </div>
        )}
        {needsReupload && (
          <div className="text-xs text-amber-700 mt-0.5 font-medium">File lost — please re-upload</div>
        )}
      </div>
      <div className="ml-3 shrink-0 flex items-center gap-1.5">
        <label className="cursor-pointer rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-500 hover:bg-neutral-50">
          {uploading ? "Uploading…" : needsReupload ? "Re-upload" : hasFile ? "Replace" : "+ Upload"}
          <input
            type="file"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              onUploadFile(doc?.id ?? uid(), label, file);
              e.target.value = "";
            }}
          />
        </label>
        {hasFile && !uploading && (removable && onRemoveDoc ? (
          <button onClick={() => { if (confirm("Remove this document?")) onRemoveDoc(doc!.id); }} className="text-neutral-300 hover:text-red-500 text-lg leading-none">��</button>
        ) : onClearFile ? (
          <button onClick={() => { if (confirm("Remove this file?")) onClearFile(doc!.id); }} className="text-neutral-300 hover:text-red-500 text-lg leading-none">×</button>
        ) : null)}
      </div>
    </div>
  );
}

// ─── Team Contact Form ──────────────────────────────────────────────────────

function TeamContactForm({ initial, roles, structures, onSave, onCancel }: {
  initial: TeamContact;
  roles: { value: TeamContactRole; label: string }[];
  structures: Structure[];
  onSave: (c: TeamContact) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<TeamContact>({ ...initial });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-neutral-500">Role</label>
          <select className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as TeamContactRole }))}>
            {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {form.role === "other" && (
          <div>
            <label className="text-xs text-neutral-500">Custom role</label>
            <input className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.roleLabel ?? ""} onChange={(e) => setForm((p) => ({ ...p, roleLabel: e.target.value }))} placeholder="e.g. Quantity Surveyor" />
          </div>
        )}
      </div>
      <div>
        <label className="text-xs text-neutral-500">Name *</label>
        <input className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Full name" />
      </div>
      <div>
        <label className="text-xs text-neutral-500">Company</label>
        <input className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.company ?? ""} onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))} placeholder="Company or practice" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-neutral-500">Email</label>
          <input type="email" className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.email ?? ""} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
        </div>
        <div>
          <label className="text-xs text-neutral-500">Phone</label>
          <input type="tel" className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" value={form.phone ?? ""} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="0400 000 000" />
        </div>
      </div>
      <div>
        <label className="text-xs text-neutral-500">Notes</label>
        <textarea className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" rows={2} value={form.notes ?? ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Any notes about this contact" />
      </div>
      {structures.length > 0 && (
        <div>
          <label className="text-xs text-neutral-500">Linked structures (optional)</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {structures.map((s) => {
              const linked = (form.associatedStructureIds ?? []).includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => {
                  const ids = form.associatedStructureIds ?? [];
                  setForm((p) => ({ ...p, associatedStructureIds: linked ? ids.filter((i) => i !== s.id) : [...ids, s.id] }));
                }} className={`rounded-full px-2.5 py-1 text-xs border ${linked ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-neutral-200 text-neutral-500"}`}>
                  {s.displayName || s.entityType}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50">Cancel</button>
        <button onClick={() => { if (!form.name.trim()) return; onSave(form); }} disabled={!form.name.trim()} className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VERIFICATION_SLOTS = [
  "Passport",
  "Driver's licence",
  "Birth certificate",
  "Proof of address",
  "Employment letter",
  "Other ID",
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Non-binary", label: "Non-binary" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

const CITIZENSHIP_OPTIONS = [
  { value: "Australian citizen", label: "Australian citizen" },
  { value: "Permanent resident", label: "Permanent resident" },
  { value: "Other", label: "Other" },
];

const TAX_RESIDENCY_OPTIONS = [
  { value: "Australian resident for tax", label: "Australian resident for tax" },
  { value: "Non-resident", label: "Non-resident" },
  { value: "Foreign resident", label: "Foreign resident" },
];

const EMPLOYMENT_OPTIONS = [
  { value: "payg", label: "PAYG" },
  { value: "self-employed", label: "Self-employed" },
  { value: "both", label: "Both" },
];

const MEMBER_EMPLOYMENT_OPTIONS = [
  { value: "payg", label: "PAYG" },
  { value: "self-employed", label: "Self-employed" },
  { value: "both", label: "Both" },
  { value: "not-employed", label: "Not employed" },
];

const RELATIONSHIP_TYPE_OPTIONS: { value: PersonRelationship["type"]; label: string }[] = [
  { value: "spouse", label: "Spouse" },
  { value: "partner", label: "Partner" },
  { value: "child", label: "Child" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "business-partner", label: "Business partner" },
  { value: "co-borrower", label: "Co-borrower" },
  { value: "guarantor", label: "Guarantor" },
  { value: "beneficiary", label: "Beneficiary" },
  { value: "other", label: "Other" },
];

const TAX_DOC_TYPE_OPTIONS = [
  { value: "tax-return", label: "Tax return" },
  { value: "noa", label: "Notice of assessment" },
  { value: "payg", label: "PAYG summary" },
  { value: "other", label: "Other" },
];

const COLLAPSED_RESET = {
  personal: true, financial: true, strategy: true, relationships: true, structures: true,
  verification: true, taxdocs: true, payslips: true,
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function ProfilePage({
  profile,
  structures,
  portfolioProperties,
  onSave,
  onAddStructure,
  onUpdateStructure,
  onDeleteStructure,
}: {
  profile: InvestorProfile;
  structures: Structure[];
  portfolioProperties: PropertyRecord[];
  onSave: (p: InvestorProfile) => void;
  onAddStructure: (s: Structure) => void;
  onUpdateStructure: (s: Structure) => void;
  onDeleteStructure: (id: string) => void;
}) {
  const [draft, setDraft] = useState<InvestorProfile>({ ...profile });
  const draftRef = useRef<InvestorProfile>(draft);
  draftRef.current = draft;
  const [savedToast, setSavedToast] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(COLLAPSED_RESET);
  const [activeMemberId, setActiveMemberId] = useState<string | null | "structures">(null);
  const [memberRemoveConfirm, setMemberRemoveConfirm] = useState<string | null>(null);
  const [structureModal, setStructureModal] = useState<{ structure: Structure | null } | null>(null);
  const [deleteStructureConfirm, setDeleteStructureConfirm] = useState<string | null>(null);
  const [docUploading, setDocUploading] = useState<string | null>(null);
  const [docUploadError, setDocUploadError] = useState<string | null>(null);

  // ── Profile document migration (detect stale blob: URLs) ──
  useEffect(() => {
    const migrated = migrateProfileDocs(
      draft.verificationDocuments ?? [],
      draft.taxDocuments ?? [],
      draft.payslips ?? [],
    );
    if (migrated.changed) {
      const updated = {
        ...draft,
        verificationDocuments: migrated.verificationDocuments,
        taxDocuments: migrated.taxDocuments,
        payslips: migrated.payslips,
      };
      setDraft(updated);
      onSave(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Supabase Storage helpers (wrap shared userStorage lib) ──
  // The category subfolder pattern lives here — pathPrefix becomes
  // `${user.id}/verification`, `${user.id}/tax`, `${user.id}/payslips`.

  async function uploadToStorage(category: string, file: File): Promise<{ filePath: string; fileSize: number } | null> {
    if (!isSupabaseEnabled() || !supabase) return null;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const result = await sharedUploadToStorage("profile-documents", `${user.id}/${category}`, file);
    if (!result.ok) {
      setDocUploadError(result.error);
      return null;
    }
    return result.result;
  }

  function downloadFromStorage(filePath: string) {
    return sharedDownloadFromStorage("profile-documents", filePath);
  }

  function deleteFromStorage(filePath: string) {
    return sharedDeleteFromStorage("profile-documents", filePath);
  }

  function toggleCollapse(id: string) {
    setCollapsed((p) => ({ ...p, [id]: !p[id] }));
  }

  function saveProfile() {
    const toSave = draftRef.current;
    onSave(toSave);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setSavedToast(true);
    savedTimer.current = setTimeout(() => setSavedToast(false), 2000);
  }

  function saveDirectly(updated: InvestorProfile) {
    setDraft(updated);
    onSave(updated);
  }

  // ── Active person context ──────────────────────────────────────────────────

  const householdMembers: HouseholdMember[] = draft.householdMembers ?? [];
  const activeMember: HouseholdMember | null =
    activeMemberId && activeMemberId !== "structures"
      ? (householdMembers.find((m) => m.id === activeMemberId) ?? null)
      : null;

  function saveMemberSection(updates: Partial<InvestorProfile>) {
    if (!activeMemberId || !activeMember) return;
    const newMembers = householdMembers.map((m) =>
      m.id === activeMemberId ? { ...m, ...updates } : m
    );
    const newDraft = { ...draft, householdMembers: newMembers };
    setDraft(newDraft);
    onSave(newDraft);
  }

  function saveMemberDirectly(updatedMember: HouseholdMember) {
    if (!activeMemberId) return;
    const newMembers = householdMembers.map((m) =>
      m.id === activeMemberId ? updatedMember : m
    );
    const newDraft = { ...draft, householdMembers: newMembers };
    setDraft(newDraft);
    onSave(newDraft);
  }

  const basePerson = (activeMember ?? draft) as Partial<InvestorProfile>;

  // Update a field for whichever person is active (primary or member)
  function updateField<K extends keyof InvestorProfile>(key: K, value: InvestorProfile[K]) {
    if (activeMember) {
      saveMemberSection({ [key]: value } as Partial<InvestorProfile>);
    } else {
      setDraft((p) => ({ ...p, [key]: value }));
    }
  }

  // Save primary draft immediately (for checkbox/button actions that don't blur)
  function saveProfileNow(partial?: Partial<InvestorProfile>) {
    const toSave = partial ? { ...draftRef.current, ...partial } : draftRef.current;
    if (partial) setDraft(toSave);
    onSave(toSave);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setSavedToast(true);
    savedTimer.current = setTimeout(() => setSavedToast(false), 2000);
  }

  // ── 1. Personal Details ────────────────────────────────────────────────────

  const pd = basePerson;
  const personalSummary = [pd.preferredName || pd.fullLegalName, pd.email].filter(Boolean).join(" · ") || "Not set";

  // ── 2. Financial Profile ───────────────────────────────────────────────────

  const fd = basePerson;
  const financialSummary = fd.annualPreTaxIncome
    ? `${currency(fd.annualPreTaxIncome)}/yr · ${fd.employmentType?.toUpperCase() ?? ""}`
    : "Not set";

  function taxSettingsFrom(source: Partial<InvestorProfile>) {
    return {
      medicareExempt: source.medicareExempt ?? false,
      medicareReduction: source.medicareReduction ?? false,
      hasPrivateHospitalCover: source.hasPrivateHospitalCover ?? false,
      medicareLevy: source.medicareLevy,
      hasHECS: source.hasHECS ?? false,
    };
  }

  function mlsNote(income: number, exempt: boolean, hasCover: boolean): string | null {
    if (exempt) return "Medicare levy exempt";
    if (hasCover) return "MLS exempt (private hospital cover)";
    if (income > 144000) return "Medicare Levy Surcharge applies: 1.5%";
    if (income > 120000) return "Medicare Levy Surcharge applies: 1.25%";
    if (income > 93000) return "Medicare Levy Surcharge applies: 1%";
    return null;
  }

  function effectiveRateLabel(income: number, exempt: boolean, hasCover: boolean, levyOverride?: number, hasHECS?: boolean): string {
    if (!income) return "";
    const rate = calculateEffectiveRate(income, { medicareExempt: exempt, hasPrivateHospitalCover: hasCover, medicareLevy: levyOverride, hasHECS });
    if (exempt) return `Effective rate: ~${rate.toFixed(1)}% (Medicare: exempt${hasHECS ? " + HECS" : ""})`;
    const levyPct = (levyOverride ?? 2.0).toFixed(1).replace(/\.0$/, "");
    let suffix = `${levyPct}% levy`;
    if (!hasCover && income > 93000) {
      const mls = income > 144000 ? 1.5 : income > 120000 ? 1.25 : 1;
      suffix += ` + ${mls}% MLS`;
    }
    if (hasHECS) suffix += " + HECS repayment";
    return `Effective rate: ~${rate.toFixed(1)}% (includes ${suffix})`;
  }

  const editingTaxSettings = taxSettingsFrom(basePerson);

  const reducedPeriods: ReducedIncomePeriod[] = activeMember
    ? (activeMember.reducedIncomePeriods ?? [])
    : (draft.reducedIncomePeriods ?? []);

  function addReducedPeriod() {
    const newPeriod = { id: uid(), label: "", start: "", end: "", annualPreTaxPay: 0 };
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, reducedIncomePeriods: [...reducedPeriods, newPeriod] });
    } else {
      saveDirectly({ ...draft, reducedIncomePeriods: [...reducedPeriods, newPeriod] });
    }
  }

  function updateReducedPeriod(id: string, field: keyof ReducedIncomePeriod, value: string | number) {
    const updated = reducedPeriods.map((p) =>
      p.id === id ? { ...p, [field]: field === "annualPreTaxPay" ? (parseInt(String(value), 10) || 0) : value } : p
    );
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, reducedIncomePeriods: updated });
    } else {
      saveDirectly({ ...draft, reducedIncomePeriods: updated });
    }
  }

  function removeReducedPeriod(id: string) {
    const filtered = reducedPeriods.filter((p) => p.id !== id);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, reducedIncomePeriods: filtered });
    } else {
      saveDirectly({ ...draft, reducedIncomePeriods: filtered });
    }
  }

  // ── 3. Relationships + Tab bar helpers ────────────────────────────────────

  function getMemberLabel(m: HouseholdMember, index: number): string {
    const typeLabel = m.memberType === "dependent" ? "Dependent" : m.memberType === "partner" ? "Partner" : "";
    const name = m.preferredName || m.fullLegalName?.split(" ")[0] || "";
    if (name && typeLabel) return `${name} (${typeLabel})`;
    if (name) return name;
    if (typeLabel) return typeLabel;
    return `Person ${index + 2}`;
  }

  function switchTab(id: string | null | "structures") {
    setActiveMemberId(id);
    setCollapsed(COLLAPSED_RESET);
    setDeleteStructureConfirm(null);
  }

  function handleAddPerson(memberType: "partner" | "dependent" = "partner") {
    const newMember: HouseholdMember = {
      id: uid(),
      memberType,
      fullLegalName: "",
      annualPreTaxIncome: memberType === "dependent" ? 0 : undefined,
      relationships: [],
      verificationDocuments: [],
      taxDocuments: [],
      payslips: [],
    };
    const updated = { ...draft, householdMembers: [...householdMembers, newMember] };
    setDraft(updated);
    onSave(updated);
    setActiveMemberId(newMember.id);
    setCollapsed(COLLAPSED_RESET);
  }

  function handleRemoveMember(id: string) {
    const newMembers = householdMembers.filter((m) => m.id !== id);
    const newDraft = { ...draft, householdMembers: newMembers };
    setDraft(newDraft);
    onSave(newDraft);
    setMemberRemoveConfirm(null);
    if (activeMemberId === id) {
      setActiveMemberId(null);
      setCollapsed(COLLAPSED_RESET);
    }
  }

  // Primary investor relationships
  const primaryRelationships: PersonRelationship[] = draft.relationships ?? [];

  function addPrimaryRelationship() {
    const first = householdMembers[0];
    if (!first) return;
    const newRel: PersonRelationship = { id: uid(), toMemberId: first.id, type: "spouse" };
    saveDirectly({ ...draft, relationships: [...primaryRelationships, newRel] });
  }

  function updatePrimaryRelationship(relId: string, fields: Partial<PersonRelationship>) {
    saveDirectly({ ...draft, relationships: primaryRelationships.map((r) => r.id === relId ? { ...r, ...fields } : r) });
  }

  function removePrimaryRelationship(relId: string) {
    saveDirectly({ ...draft, relationships: primaryRelationships.filter((r) => r.id !== relId) });
  }

  // Member relationships (direct save)
  const memberRelationships: PersonRelationship[] = activeMember?.relationships ?? [];

  function addMemberRelationship() {
    if (!activeMember) return;
    const newRel: PersonRelationship = { id: uid(), toMemberId: "primary", type: "spouse" };
    saveMemberDirectly({ ...activeMember, relationships: [...memberRelationships, newRel] });
  }

  function updateMemberRelationship(relId: string, fields: Partial<PersonRelationship>) {
    if (!activeMember) return;
    saveMemberDirectly({ ...activeMember, relationships: memberRelationships.map((r) => r.id === relId ? { ...r, ...fields } : r) });
  }

  function removeMemberRelationship(relId: string) {
    if (!activeMember) return;
    saveMemberDirectly({ ...activeMember, relationships: memberRelationships.filter((r) => r.id !== relId) });
  }

  const activeRelationships = activeMemberId === null ? primaryRelationships : memberRelationships;
  const relationshipsSummary = activeRelationships.length > 0
    ? `${activeRelationships.length} relationship${activeRelationships.length !== 1 ? "s" : ""}`
    : "No relationships added";

  // Tab bar derived values
  const combinedHouseholdIncome = (draft.annualPreTaxIncome ?? 0) +
    householdMembers.reduce((sum, m) => sum + (m.annualPreTaxIncome ?? 0), 0);
  const incomeContributors = [
    draft.annualPreTaxIncome ?? 0,
    ...householdMembers.map((m) => m.annualPreTaxIncome ?? 0),
  ].filter((i) => i > 0).length;
  const hasPrimaryName = !!(draft.preferredName || draft.fullLegalName);
  const primaryTabLabel = draft.preferredName || draft.fullLegalName?.split(" ")[0] || "Me";

  // ── 5. My Structures ───────────────────────────────────────────────────────


  // ── 6. Verification Documents ──────────────────────────────────────────────

  const verDocs: VerificationDocument[] = activeMember
    ? (activeMember.verificationDocuments ?? [])
    : (draft.verificationDocuments ?? []);
  const uploadedCount = verDocs.filter((d) => d.filename).length;
  const totalSlots = VERIFICATION_SLOTS.length + verDocs.filter((d) => !VERIFICATION_SLOTS.includes(d.label)).length;
  const verSummary = `${uploadedCount} of ${totalSlots} documents uploaded`;

  async function uploadVerDoc(docId: string, label: string, file: File) {
    setDocUploading(docId);
    setDocUploadError(null);
    const result = await uploadToStorage("verification", file);
    setDocUploading(null);
    if (!result) return;
    const existing = verDocs.find((d) => d.id === docId);
    // Delete old storage file if replacing
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const doc: VerificationDocument = {
      id: docId, label,
      filename: file.name,
      dateUploaded: new Date().toISOString().slice(0, 10),
      filePath: result.filePath,
      fileSize: result.fileSize,
      needsReupload: false,
      url: undefined,
    };
    const updated = existing ? verDocs.map((d) => d.id === docId ? { ...d, ...doc } : d) : [...verDocs, doc];
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, verificationDocuments: updated });
    } else {
      saveDirectly({ ...draft, verificationDocuments: updated });
    }
  }

  async function clearVerDoc(docId: string) {
    const existing = verDocs.find((d) => d.id === docId);
    if (!existing) return;
    if (existing.filePath) await deleteFromStorage(existing.filePath);
    const cleared = verDocs.map((d) => d.id === docId ? {
      id: d.id, label: d.label, notes: d.notes,
      filename: undefined, dateUploaded: undefined,
      filePath: undefined, fileSize: undefined,
      needsReupload: false, url: undefined,
    } : d);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, verificationDocuments: cleared });
    } else {
      saveDirectly({ ...draft, verificationDocuments: cleared });
    }
  }

  async function removeCustomVerDoc(docId: string) {
    const existing = verDocs.find((d) => d.id === docId);
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const filtered = verDocs.filter((d) => d.id !== docId);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, verificationDocuments: filtered });
    } else {
      saveDirectly({ ...draft, verificationDocuments: filtered });
    }
  }

  function addCustomVerDoc() {
    const newDoc = { id: uid(), label: "Custom document" };
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, verificationDocuments: [...verDocs, newDoc] });
    } else {
      saveDirectly({ ...draft, verificationDocuments: [...verDocs, newDoc] });
    }
  }

  // ── 7. Tax Documents ───────────────────────────────────────────────────────

  const rawTaxDocs = activeMember ? (activeMember.taxDocuments ?? []) : (draft.taxDocuments ?? []);
  const taxDocs: TaxDocument[] = [...rawTaxDocs].sort((a, b) => b.financialYear.localeCompare(a.financialYear));
  const taxSummary = `${taxDocs.length} tax year${taxDocs.length !== 1 ? "s" : ""} on file`;

  function addTaxDoc() {
    const newDoc: TaxDocument = { id: uid(), financialYear: "", type: "tax-return", label: "" };
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, taxDocuments: [newDoc, ...rawTaxDocs] });
    } else {
      saveDirectly({ ...draft, taxDocuments: [newDoc, ...rawTaxDocs] });
    }
  }

  function updateTaxDoc(id: string, field: keyof TaxDocument, value: string) {
    const updated = rawTaxDocs.map((d) => d.id === id ? { ...d, [field]: value } : d);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, taxDocuments: updated });
    } else {
      saveDirectly({ ...draft, taxDocuments: updated });
    }
  }

  async function uploadTaxDoc(id: string, file: File) {
    setDocUploading(id);
    setDocUploadError(null);
    const result = await uploadToStorage("tax", file);
    setDocUploading(null);
    if (!result) return;
    const existing = rawTaxDocs.find((d) => d.id === id);
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const updated = rawTaxDocs.map((d) =>
      d.id === id ? { ...d, filename: file.name, dateUploaded: new Date().toISOString().slice(0, 10), filePath: result.filePath, fileSize: result.fileSize, needsReupload: false, url: undefined } : d
    );
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, taxDocuments: updated });
    } else {
      saveDirectly({ ...draft, taxDocuments: updated });
    }
  }

  async function removeTaxDoc(id: string) {
    const existing = rawTaxDocs.find((d) => d.id === id);
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const filtered = rawTaxDocs.filter((d) => d.id !== id);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, taxDocuments: filtered });
    } else {
      saveDirectly({ ...draft, taxDocuments: filtered });
    }
  }

  // ── 8. Payslips ────────────────────────────────────────────────────────────

  const payslips: PayslipEntry[] = activeMember ? (activeMember.payslips ?? []) : (draft.payslips ?? []);
  const payslipSummary = `${payslips.length} payslip${payslips.length !== 1 ? "s" : ""} on file`;

  function addPayslip() {
    const newSlip = { id: uid(), date: "", employer: "" };
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, payslips: [...payslips, newSlip] });
    } else {
      saveDirectly({ ...draft, payslips: [...payslips, newSlip] });
    }
  }

  function updatePayslip(id: string, field: keyof PayslipEntry, value: string) {
    const updated = payslips.map((p) => p.id === id ? { ...p, [field]: value } : p);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, payslips: updated });
    } else {
      saveDirectly({ ...draft, payslips: updated });
    }
  }

  async function uploadPayslip(id: string, file: File) {
    setDocUploading(id);
    setDocUploadError(null);
    const result = await uploadToStorage("payslips", file);
    setDocUploading(null);
    if (!result) return;
    const existing = payslips.find((p) => p.id === id);
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const updated = payslips.map((p) =>
      p.id === id ? { ...p, filename: file.name, dateUploaded: new Date().toISOString().slice(0, 10), filePath: result.filePath, fileSize: result.fileSize, needsReupload: false, url: undefined } : p
    );
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, payslips: updated });
    } else {
      saveDirectly({ ...draft, payslips: updated });
    }
  }

  async function removePayslip(id: string) {
    const existing = payslips.find((p) => p.id === id);
    if (existing?.filePath) await deleteFromStorage(existing.filePath);
    const filtered = payslips.filter((p) => p.id !== id);
    if (activeMember) {
      saveMemberDirectly({ ...activeMember, payslips: filtered });
    } else {
      saveDirectly({ ...draft, payslips: filtered });
    }
  }

  // ── 9. Team Contacts ────────────────────────────────────────────────────

  const TEAM_ROLES: { value: TeamContactRole; label: string }[] = [
    { value: "broker", label: "Broker" },
    { value: "mortgage-broker", label: "Mortgage Broker" },
    { value: "accountant", label: "Accountant" },
    { value: "financial-adviser", label: "Financial Adviser" },
    { value: "solicitor", label: "Solicitor" },
    { value: "buyer-agent", label: "Buyer's Agent" },
    { value: "conveyancer", label: "Conveyancer" },
    { value: "building-inspector", label: "Building Inspector" },
    { value: "other", label: "Other" },
  ];

  const teamContacts: TeamContact[] = draft.teamContacts ?? [];
  const teamSummary = teamContacts.length === 0 ? "No contacts yet" : `${teamContacts.length} contact${teamContacts.length !== 1 ? "s" : ""}`;
  const [editingContact, setEditingContact] = useState<TeamContact | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);

  function saveTeamContact(contact: TeamContact) {
    const existing = teamContacts.find((c) => c.id === contact.id);
    const updated = existing
      ? teamContacts.map((c) => c.id === contact.id ? contact : c)
      : [...teamContacts, contact];
    saveDirectly({ ...draft, teamContacts: updated });
    setShowContactModal(false);
    setEditingContact(null);
  }

  function removeTeamContact(id: string) {
    saveDirectly({ ...draft, teamContacts: teamContacts.filter((c) => c.id !== id) });
  }

  // ── Employment options by context ─────────────────────────────────────────
  const activeEmploymentOptions = activeMember ? MEMBER_EMPLOYMENT_OPTIONS : EMPLOYMENT_OPTIONS;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900">My Profile</h1>
          <p className="mt-2 text-neutral-600">Your personal and financial details</p>
        </div>
        {savedToast && (
          <span className="flex items-center gap-1 text-sm font-medium text-emerald-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Saved
          </span>
        )}
      </div>

      <TabExplainer>
        <p><strong>Your Profile is the foundation of every calculation in AusProp.</strong> Income, tax position, household members, structures (trusts, SMSF, companies), and personal documents all live here — and feed every other page.</p>
        <p><strong>Personal & income details</strong> drive your tax calculations, marginal rate, borrowing capacity estimates, and what the AI sees about your situation.</p>
        <p><strong>Household members</strong> let you model joint ownership, partner income, and shared expenses correctly. Non-dependent partners appear on the Dashboard tax cards and the Tax page lens picker. Dependents are excluded from per-adult tax allocation.</p>
        <p><strong>Structures</strong> are the legal entities you hold property through (Trusts, SMSFs, Companies, Personal). Linked to properties on the Portfolio page.</p>
        <p><strong>Verification documents, tax documents, and payslips</strong> are <em>optional</em>. They're not required to use AusProp — every calculation works without them. They exist to support a future adviser-sharing feature: one-click share of relevant documents with your broker, accountant, or mortgage broker (you choose what to share, with whom, and can revoke any time).</p>
        <p><strong>How your data is stored:</strong> Files upload directly to Supabase Storage with AES-256 encryption at rest. Row-level security means only your account can access them. Files are never shared without your explicit authorisation. See Settings for full details.</p>
        <p className="text-amber-700"><strong>Heads up:</strong> Don't upload tax file numbers, full bank account numbers, or passwords. AusProp doesn't need them and they shouldn't be in any document you upload.</p>
      </TabExplainer>

      {/* B103 Phase 4c-3 fix (P1) — surface the household-members tax-allocation
          semantic above the tab bar. The TabExplainer at the top of the page
          already contains the same copy but is collapsed by default ("What
          does this tab show?"); users won't expand it. Render the key line
          inline so it's always visible at the top of the household-members
          area. */}
      <p className="mb-3 text-xs text-neutral-500">
        Household members let you model joint ownership, partner income, and shared expenses correctly. <strong className="text-neutral-700">Non-dependent partners</strong> appear on the Dashboard tax cards and the Tax page lens picker. <strong className="text-neutral-700">Dependents</strong> are excluded from per-adult tax allocation.
      </p>

      {/* Tab bar */}
      <div>
        <div className="flex flex-wrap gap-2 mb-3">
          {hasPrimaryName && (
            <button
              onClick={() => switchTab(null)}
              className={activeMemberId === null
                ? "rounded-full bg-neutral-900 text-white px-4 py-1.5 text-sm font-medium"
                : "rounded-full border border-neutral-200 bg-white text-neutral-600 px-4 py-1.5 text-sm hover:border-neutral-400"}
            >
              {primaryTabLabel}
            </button>
          )}
          {householdMembers.map((m, i) => (
            <div key={m.id} className="flex items-center gap-1">
              <button
                onClick={() => switchTab(m.id)}
                className={activeMemberId === m.id
                  ? "rounded-full bg-neutral-900 text-white px-4 py-1.5 text-sm font-medium"
                  : "rounded-full border border-neutral-200 bg-white text-neutral-600 px-4 py-1.5 text-sm hover:border-neutral-400"}
              >
                {getMemberLabel(m, i)}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setMemberRemoveConfirm(m.id); }}
                className="h-5 w-5 rounded-full border border-neutral-200 bg-white text-xs text-neutral-400 hover:border-red-300 hover:text-red-500 flex items-center justify-center"
                title={`Remove ${getMemberLabel(m, i)}`}
              >
                ×
              </button>
            </div>
          ))}
          {/* B103 Phase 4c-3 — InfoTooltips clarify the tax-allocation
              semantic of partner vs dependent. memberType is set once at
              creation; switching post-creation requires delete+re-add
              (B105 in BACKLOG). */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleAddPerson("partner")}
              className="rounded-full border border-dashed border-neutral-300 text-neutral-400 px-4 py-1.5 text-sm hover:border-neutral-500 hover:text-neutral-600"
            >
              + Add partner
            </button>
            <InfoTooltip tip="A non-dependent adult — co-investor, working spouse. Gets their own tax-allocation card on the Dashboard and the Tax-page lens picker." />
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleAddPerson("dependent")}
              className="rounded-full border border-dashed border-neutral-300 text-neutral-400 px-4 py-1.5 text-sm hover:border-neutral-500 hover:text-neutral-600"
            >
              + Add dependent
            </button>
            <InfoTooltip tip="A child, or a partner on parental leave with no PAYG income this FY. Excluded from per-adult tax allocation." />
          </div>
          <button
            onClick={() => switchTab("structures")}
            className={activeMemberId === "structures"
              ? "rounded-full bg-neutral-900 text-white px-4 py-1.5 text-sm font-medium"
              : "rounded-full border border-neutral-200 bg-white text-neutral-600 px-4 py-1.5 text-sm hover:border-neutral-400"}
          >
            My Structures
          </button>
        </div>

        {incomeContributors >= 2 && (
          <p className="text-sm text-neutral-400 mb-3">
            Combined household income:{" "}
            <span className="font-medium text-neutral-700">{currency(combinedHouseholdIncome)}</span>
            /yr across {incomeContributors} people
          </p>
        )}

        {memberRemoveConfirm && (() => {
          const target = householdMembers.find((m) => m.id === memberRemoveConfirm);
          if (!target) return null;
          const name = target.preferredName || target.fullLegalName || "this person";
          return (
            <div className="mb-3 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm">
              <span className="text-red-800">Remove <strong>{name}</strong> from your profile?</span>
              <button
                onClick={() => handleRemoveMember(memberRemoveConfirm)}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500"
              >
                Remove
              </button>
              <button
                onClick={() => setMemberRemoveConfirm(null)}
                className="text-xs text-neutral-500 hover:text-neutral-700"
              >
                Cancel
              </button>
            </div>
          );
        })()}
      </div>

      {/* ── My Structures tab ──────────────────────────────────────────── */}
      {activeMemberId === "structures" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">My Structures</h2>
              <p className="mt-0.5 text-sm text-neutral-500">Trusts, companies, and SMSFs used to hold your properties.</p>
            </div>
            <button
              onClick={() => setStructureModal({ structure: null })}
              className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700"
            >
              + Add structure
            </button>
          </div>

          {structures.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 px-6 py-10 text-center">
              <p className="text-sm text-neutral-500">No structures yet. Add a trust, company or SMSF to get started.</p>
              <button
                onClick={() => setStructureModal({ structure: null })}
                className="mt-4 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700"
              >
                + Add structure
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {structures.map((s) => {
                const propCount = portfolioProperties.filter((p) => p.structureId === s.id).length;
                const isConfirmingDelete = deleteStructureConfirm === s.id;
                return (
                  <div key={s.id} className="rounded-2xl border border-neutral-200 bg-white px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-neutral-900">{s.displayName}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">{s.entityType}</span>
                          {s.abn && <span className="text-xs text-neutral-400">ABN {s.abn}</span>}
                          <span className="text-xs text-neutral-400">{propCount} {propCount === 1 ? "property" : "properties"}</span>
                        </div>
                        {s.beneficiaries && s.beneficiaries.length > 0 && (
                          <div className="mt-1.5 text-xs text-neutral-400">
                            Beneficiaries: {s.beneficiaries.map((b) => b.name).join(", ")}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          onClick={() => setStructureModal({ structure: s })}
                          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs text-neutral-600 hover:bg-neutral-50"
                        >
                          Edit
                        </button>
                        {isConfirmingDelete ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-700">Delete?</span>
                            <button
                              onClick={() => { onDeleteStructure(s.id); setDeleteStructureConfirm(null); }}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteStructureConfirm(null)}
                              className="text-xs text-neutral-500 hover:text-neutral-700"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteStructureConfirm(s.id)}
                            className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs text-neutral-400 hover:border-red-300 hover:text-red-500"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {structureModal && (
            <StructureModal
              structure={structureModal.structure}
              profile={profile}
              onSave={(s) => {
                if (structureModal.structure) {
                  onUpdateStructure(s);
                } else {
                  onAddStructure(s);
                }
                setStructureModal(null);
              }}
              onClose={() => setStructureModal(null)}
            />
          )}
        </div>
      )}

      {/* Two-column layout */}
      {activeMemberId !== "structures" && <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

        {/* ── LEFT COLUMN ──────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* 1. Personal Details */}
          <CollapsibleSection
            id="personal" title="Personal Details" summary={personalSummary}
            collapsed={collapsed.personal} onToggleCollapse={() => toggleCollapse("personal")}
          >
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Full legal name" value={pd.fullLegalName} onChange={(v) => updateField("fullLegalName", v)} onBlur={activeMember ? undefined : saveProfile} />
              <EditField label="Preferred name" value={pd.preferredName} onChange={(v) => updateField("preferredName", v)} onBlur={activeMember ? undefined : saveProfile} />
              <EditField label="Date of birth" type="date" value={pd.dateOfBirth} onChange={(v) => updateField("dateOfBirth", v)} onBlur={activeMember ? undefined : saveProfile} />
              <SelectField label="Gender" value={pd.gender} options={GENDER_OPTIONS} onChange={(v) => { updateField("gender", v); if (!activeMember) saveProfileNow({ gender: v }); }} />
              <div className="col-span-2">
                <EditField label="Current address" value={pd.currentAddress} onChange={(v) => updateField("currentAddress", v)} onBlur={activeMember ? undefined : saveProfile} />
              </div>
              <EditField label="Phone" value={pd.phone} onChange={(v) => updateField("phone", v)} onBlur={activeMember ? undefined : saveProfile} />
              <EditField label="Email" type="email" value={pd.email} onChange={(v) => updateField("email", v)} onBlur={activeMember ? undefined : saveProfile} />
              <EditField label="TFN" value={pd.tfn} onChange={(v) => updateField("tfn", v)} onBlur={activeMember ? undefined : saveProfile} />
              <SelectField label="Citizenship status" value={pd.citizenshipStatus} options={CITIZENSHIP_OPTIONS} onChange={(v) => { updateField("citizenshipStatus", v); if (!activeMember) saveProfileNow({ citizenshipStatus: v }); }} />
              <SelectField label="Tax residency" value={pd.taxResidencyStatus} options={TAX_RESIDENCY_OPTIONS} onChange={(v) => { updateField("taxResidencyStatus", v); if (!activeMember) saveProfileNow({ taxResidencyStatus: v }); }} />
            </div>
          </CollapsibleSection>

          {/* 2. Financial Profile — hidden for dependents (no income/tax data) */}
          {!(activeMember?.memberType === "dependent") && <CollapsibleSection
            id="financial" title="Financial Profile" summary={financialSummary}
            collapsed={collapsed.financial} onToggleCollapse={() => toggleCollapse("financial")}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <EditField
                  label="Annual pre-tax income"
                  type="number"
                  value={fd.annualPreTaxIncome}
                  onChange={(v) => {
                    const inc = parseInt(v, 10) || 0;
                    if (activeMember) {
                      saveMemberSection({ annualPreTaxIncome: inc, marginalTaxRate: calculateMarginalRate(inc) });
                    } else {
                      setDraft((p) => ({ ...p, annualPreTaxIncome: inc, marginalTaxRate: calculateMarginalRate(inc) }));
                    }
                  }}
                  onBlur={activeMember ? undefined : saveProfile}
                />
                <SelectField label="Employment type" value={fd.employmentType} options={activeEmploymentOptions} onChange={(v) => { updateField("employmentType", v as InvestorProfile["employmentType"]); if (!activeMember) saveProfileNow({ employmentType: v as InvestorProfile["employmentType"] }); }} />
                <EditField label="Employer name" value={fd.employerName} onChange={(v) => updateField("employerName", v)} onBlur={activeMember ? undefined : saveProfile} />
                <EditField label="Employer ABN" value={fd.employerAbn} onChange={(v) => updateField("employerAbn", v)} onBlur={activeMember ? undefined : saveProfile} />
                <div>
                  <p className="mb-1 text-xs text-neutral-400">Auto-calculated from income · you can override below</p>
                  <EditField
                    label="Marginal tax rate (%)"
                    type="number"
                    value={fd.marginalTaxRate}
                    onChange={(v) => updateField("marginalTaxRate", parseFloat(v) || 0)}
                    onBlur={activeMember ? undefined : saveProfile}
                  />
                </div>
                <EditField
                  label="Monthly net contribution"
                  type="number"
                  value={fd.monthlyNetContribution}
                  onChange={(v) => updateField("monthlyNetContribution", parseInt(v, 10) || 0)}
                  onBlur={activeMember ? undefined : saveProfile}
                />
                <EditField
                  label="PAYG withheld (annual)"
                  type="number"
                  value={fd.paygWithheld}
                  onChange={(v) => updateField("paygWithheld", parseInt(v, 10) || 0)}
                  onBlur={activeMember ? undefined : saveProfile}
                  helper="Total tax withheld by your employer for the year. Used to show estimated refund/owing on the Dashboard. Find this on your payment summaries."
                />
              </div>

              {/* Tax settings */}
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Medicare &amp; Levy settings</div>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={fd.medicareExempt ?? false}
                    onChange={(e) => {
                      if (activeMember) saveMemberSection({ medicareExempt: e.target.checked });
                      else saveProfileNow({ medicareExempt: e.target.checked });
                    }}
                  />
                  Exempt from Medicare levy (e.g. Defence Force, certain visa holders)
                </label>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={fd.medicareReduction ?? false}
                    onChange={(e) => {
                      if (activeMember) saveMemberSection({ medicareReduction: e.target.checked });
                      else saveProfileNow({ medicareReduction: e.target.checked });
                    }}
                  />
                  Eligible for Medicare levy reduction (low income threshold)
                </label>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={fd.hasPrivateHospitalCover ?? false}
                    onChange={(e) => {
                      if (activeMember) saveMemberSection({ hasPrivateHospitalCover: e.target.checked });
                      else saveProfileNow({ hasPrivateHospitalCover: e.target.checked });
                    }}
                  />
                  Private hospital insurance cover (exempts from Medicare Levy Surcharge)
                </label>
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={fd.hasHECS ?? false}
                    onChange={(e) => {
                      if (activeMember) saveMemberSection({ hasHECS: e.target.checked });
                      else saveProfileNow({ hasHECS: e.target.checked });
                    }}
                  />
                  HECS/HELP debt (adds compulsory repayment to effective rate)
                </label>
                {!editingTaxSettings.medicareExempt && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <EditField
                      label="Medicare levy override (%)"
                      type="number"
                      value={fd.medicareLevy ?? 2.0}
                      onChange={(v) => updateField("medicareLevy", parseFloat(v) || 2.0)}
                      onBlur={activeMember ? undefined : saveProfile}
                      placeholder="2.0"
                    />
                  </div>
                )}
                {(() => {
                  const inc = fd.annualPreTaxIncome ?? 0;
                  const note = mlsNote(inc, editingTaxSettings.medicareExempt, editingTaxSettings.hasPrivateHospitalCover);
                  const effLabel = effectiveRateLabel(inc, editingTaxSettings.medicareExempt, editingTaxSettings.hasPrivateHospitalCover, editingTaxSettings.medicareLevy, editingTaxSettings.hasHECS);
                  return (
                    <div className="space-y-1 pt-1">
                      {note && <p className="text-xs text-neutral-500">{note}</p>}
                      {effLabel && <p className="text-xs font-medium text-neutral-700">{effLabel}</p>}
                    </div>
                  );
                })()}
              </div>

              {/* Reduced income periods */}
              <div className="mt-2">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">Reduced income periods</div>
                {reducedPeriods.map((rp) => (
                  <div key={rp.id} className="mb-3 rounded-xl border border-neutral-100 bg-white p-3 space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3"><EditField label="Label" value={rp.label} onChange={(v) => updateReducedPeriod(rp.id, "label", v)} /></div>
                      <div className="col-span-3"><EditField label="Start" type="date" value={rp.start} onChange={(v) => updateReducedPeriod(rp.id, "start", v)} /></div>
                      <div className="col-span-3"><EditField label="End" type="date" value={rp.end} onChange={(v) => updateReducedPeriod(rp.id, "end", v)} /></div>
                      <div className="col-span-2">
                        <EditField
                          label="Income/yr"
                          type="number"
                          value={rp.annualPreTaxPay}
                          onChange={(v) => updateReducedPeriod(rp.id, "annualPreTaxPay", parseInt(v, 10) || 0)}
                        />
                      </div>
                      <button onClick={() => removeReducedPeriod(rp.id)} className="col-span-1 pb-2 text-neutral-400 hover:text-red-500 text-lg text-center">×</button>
                    </div>
                    {rp.annualPreTaxPay > 0 && (
                      <div className="flex gap-3">
                        <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-600">
                          Marginal: {calculateMarginalRate(rp.annualPreTaxPay)}%
                        </span>
                        <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-600">
                          Effective: ~{calculateEffectiveRate(rp.annualPreTaxPay, editingTaxSettings).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={addReducedPeriod} className="text-sm text-blue-600 hover:underline">+ Add period</button>
              </div>
            </div>
          </CollapsibleSection>}

          {/* 3. Household Goal — link to Strategy page */}
          {activeMemberId === null && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-700 m-0">Household Goal & Strategy</p>
                <p className="text-xs text-neutral-500 mt-0.5 m-0">Your investment goal, strategy and portfolio phase are set in the Strategy section.</p>
              </div>
            </div>
          )}

          {/* 4. Relationships — hidden for dependents */}
          {activeMember?.memberType !== "dependent" && (activeMemberId === null ? householdMembers.length > 0 : true) && (
            <CollapsibleSection
              id="relationships" title="Relationships" summary={relationshipsSummary}
              collapsed={collapsed.relationships} onToggleCollapse={() => toggleCollapse("relationships")}
            >
              <div className="space-y-2">
                {activeRelationships.length === 0 && (
                  <p className="text-sm text-neutral-400">No relationships added yet.</p>
                )}
                {activeRelationships.map((rel) => (
                  <div key={rel.id} className="flex items-center gap-2 rounded-xl border border-neutral-100 bg-white px-4 py-2">
                    <select
                      className="flex-1 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
                      value={rel.type}
                      onChange={(e) => {
                        const t = e.target.value as PersonRelationship["type"];
                        activeMemberId === null
                          ? updatePrimaryRelationship(rel.id, { type: t })
                          : updateMemberRelationship(rel.id, { type: t });
                      }}
                    >
                      {RELATIONSHIP_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <span className="text-xs text-neutral-400 shrink-0">to</span>
                    <select
                      className="flex-1 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm"
                      value={rel.toMemberId}
                      onChange={(e) => {
                        activeMemberId === null
                          ? updatePrimaryRelationship(rel.id, { toMemberId: e.target.value })
                          : updateMemberRelationship(rel.id, { toMemberId: e.target.value });
                      }}
                    >
                      {activeMemberId !== null && (
                        <option value="primary">{draft.preferredName || draft.fullLegalName || "Primary investor"}</option>
                      )}
                      {householdMembers
                        .filter((m) => m.id !== activeMemberId)
                        .map((m) => (
                          <option key={m.id} value={m.id}>{m.preferredName || m.fullLegalName || "Unnamed"}</option>
                        ))}
                    </select>
                    <button
                      onClick={() => activeMemberId === null ? removePrimaryRelationship(rel.id) : removeMemberRelationship(rel.id)}
                      className="text-neutral-300 hover:text-red-500 text-lg shrink-0"
                    >×</button>
                  </div>
                ))}
                <button
                  onClick={() => activeMemberId === null ? addPrimaryRelationship() : addMemberRelationship()}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add relationship
                </button>
                <p className="mt-1 text-xs text-neutral-400">
                  Relationships are informational in Phase 1 — they will drive loan applications, trust distributions and adviser access in Phase 2.
                </p>
              </div>
            </CollapsibleSection>
          )}

        </div>

        {/* ── RIGHT COLUMN ─────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* 5b. Team Contacts */}
          <CollapsibleSection
            id="team" title="My Team" summary={teamSummary}
            collapsed={collapsed.team} onToggleCollapse={() => toggleCollapse("team")}
          >
            <p className="mb-3 text-xs text-neutral-400">Your professional contacts — broker, accountant, solicitor, etc. Used to pre-fill email templates when sending reminders. Optional.</p>
            {teamContacts.length === 0 ? (
              <p className="text-sm text-neutral-400 py-2">No contacts yet. Add your broker, accountant, or other professional contacts here so reminders can pre-fill emails to them.</p>
            ) : (
              <div className="space-y-2">
                {teamContacts.map((c) => {
                  const roleLabel = c.role === "other" ? (c.roleLabel || "Other") : (TEAM_ROLES.find((r) => r.value === c.role)?.label ?? c.role);
                  const initials = c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                  return (
                    <div key={c.id} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white px-4 py-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-600 shrink-0">{initials}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-800">{c.name}</div>
                        <div className="text-xs text-neutral-400 truncate">
                          {roleLabel}{c.company ? ` · ${c.company}` : ""}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}
                        </div>
                      </div>
                      <button onClick={() => { setEditingContact(c); setShowContactModal(true); }} className="text-xs text-neutral-400 hover:text-neutral-600">Edit</button>
                      <button onClick={() => { if (confirm(`Remove ${c.name}?`)) removeTeamContact(c.id); }} className="text-neutral-300 hover:text-red-500 text-lg">×</button>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => { setEditingContact(null); setShowContactModal(true); }}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >+ Add contact</button>
          </CollapsibleSection>

          {/* Team Contact Modal */}
          {showContactModal && (() => {
            const isNew = !editingContact;
            const initial: TeamContact = editingContact ?? { id: uid(), role: "broker", name: "", createdAt: new Date().toISOString() };
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900">{isNew ? "Add Contact" : "Edit Contact"}</h3>
                  <TeamContactForm
                    initial={initial}
                    roles={TEAM_ROLES}
                    structures={structures}
                    onSave={saveTeamContact}
                    onCancel={() => { setShowContactModal(false); setEditingContact(null); }}
                  />
                </div>
              </div>
            );
          })()}

          {/* 6. Verification Documents */}
          <p className="mb-2 text-xs text-neutral-500">Optional — supports future one-click document sharing with your broker, accountant, or adviser.</p>
          <CollapsibleSection
            id="verification" title="Verification Documents" summary={verSummary}
            collapsed={collapsed.verification} onToggleCollapse={() => toggleCollapse("verification")}
          >
            <div className="space-y-2">
              {docUploadError && <div className="text-xs text-red-600 mb-1">{docUploadError}</div>}
              {VERIFICATION_SLOTS.map((label) => {
                const existing = verDocs.find((d) => d.label === label && d.filename);
                const doc = existing ?? verDocs.find((d) => d.label === label);
                return (
                  <DocUploadSlot
                    key={label}
                    label={label}
                    doc={doc}
                    onUploadFile={uploadVerDoc}
                    onDownload={downloadFromStorage}
                    onClearFile={clearVerDoc}
                    uploading={docUploading === doc?.id}
                  />
                );
              })}
              {verDocs.filter((d) => !VERIFICATION_SLOTS.includes(d.label)).map((doc) => (
                <DocUploadSlot key={doc.id} label={doc.label} doc={doc} onUploadFile={uploadVerDoc} onDownload={downloadFromStorage} onRemoveDoc={removeCustomVerDoc} removable uploading={docUploading === doc.id} />
              ))}
              <button onClick={addCustomVerDoc} className="text-sm text-blue-600 hover:underline">+ Add custom document</button>
            </div>
          </CollapsibleSection>

          {/* 7. Tax Documents */}
          <p className="mb-2 text-xs text-neutral-500">Optional — keep your historical tax documents (returns, NOAs, BAS, depreciation schedules) in one place. Future adviser-sharing feature will let you push these to your accountant.</p>
          <CollapsibleSection
            id="taxdocs" title="Tax Documents" summary={taxSummary}
            collapsed={collapsed.taxdocs} onToggleCollapse={() => toggleCollapse("taxdocs")}
          >
            <div className="space-y-3">
              {taxDocs.length === 0 && <p className="text-sm text-neutral-400">No tax documents added yet.</p>}
              {taxDocs.map((doc) => (
                <div key={doc.id} className="rounded-xl border border-neutral-100 bg-white px-4 py-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="mb-1 text-xs text-neutral-400">Financial year</div>
                      <input
                        className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-sm"
                        placeholder="2024-25"
                        value={doc.financialYear}
                        onChange={(e) => updateTaxDoc(doc.id, "financialYear", e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-neutral-400">Type</div>
                      <select
                        className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-sm"
                        value={doc.type}
                        onChange={(e) => updateTaxDoc(doc.id, "type", e.target.value)}
                      >
                        {TAX_DOC_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end gap-1">
                      <label className="cursor-pointer rounded-lg border border-dashed border-neutral-300 px-2 py-1.5 text-xs text-neutral-500 hover:bg-neutral-50">
                        {docUploading === doc.id ? "Uploading…" : doc.needsReupload ? "Re-upload" : doc.filename ? "Replace" : "+ Upload"}
                        <input type="file" className="hidden" disabled={docUploading === doc.id} onChange={(e) => { const f = e.target.files?.[0]; if (f) { void uploadTaxDoc(doc.id, f); e.target.value = ""; } }} />
                      </label>
                      <button onClick={() => removeTaxDoc(doc.id)} className="text-neutral-300 hover:text-red-500 text-lg px-1">×</button>
                    </div>
                  </div>
                  {doc.filename && (
                    <div className="mt-1 text-xs text-neutral-400 truncate flex items-center gap-1">
                      {doc.filePath ? (
                        <button onClick={() => downloadFromStorage(doc.filePath!)} className="text-blue-600 hover:underline">{doc.filename}</button>
                      ) : (
                        <span>{doc.filename}</span>
                      )}
                      {doc.dateUploaded && <span>· {doc.dateUploaded}</span>}
                    </div>
                  )}
                  {doc.needsReupload && !doc.filePath && <div className="mt-1 text-xs text-amber-700 font-medium">File lost — please re-upload</div>}
                </div>
              ))}
              <button onClick={addTaxDoc} className="text-sm text-blue-600 hover:underline">+ Add tax year</button>
            </div>
          </CollapsibleSection>

          {/* 8. Payslips */}
          <p className="mb-2 text-xs text-neutral-500">Optional — keep payslips for borrowing capacity discussions with your broker.</p>
          <CollapsibleSection
            id="payslips" title="Payslips" summary={payslipSummary}
            collapsed={collapsed.payslips} onToggleCollapse={() => toggleCollapse("payslips")}
          >
            <div className="space-y-2">
              {payslips.length === 0 && <p className="text-sm text-neutral-400">No payslips added yet.</p>}
              {payslips.map((ps) => (
                <div key={ps.id} className="flex items-end gap-2 rounded-xl border border-neutral-100 bg-white px-4 py-3">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div>
                      <div className="mb-1 text-xs text-neutral-400">Date</div>
                      <input type="date" className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-sm" value={ps.date} onChange={(e) => updatePayslip(ps.id, "date", e.target.value)} />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-neutral-400">Employer</div>
                      <input className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-sm" value={ps.employer} onChange={(e) => updatePayslip(ps.id, "employer", e.target.value)} />
                    </div>
                    {ps.filename && (
                      <div className="col-span-2 text-xs text-neutral-400 truncate">
                        {ps.filePath ? (
                          <button onClick={() => downloadFromStorage(ps.filePath!)} className="text-blue-600 hover:underline">{ps.filename}</button>
                        ) : (
                          <span>{ps.filename}</span>
                        )}
                      </div>
                    )}
                    {ps.needsReupload && !ps.filePath && <div className="col-span-2 text-xs text-amber-700 font-medium">File lost — please re-upload</div>}
                  </div>
                  <label className="cursor-pointer rounded-lg border border-dashed border-neutral-300 px-2 py-1.5 text-xs text-neutral-500 hover:bg-neutral-50 shrink-0">
                    {docUploading === ps.id ? "Uploading…" : ps.needsReupload ? "Re-upload" : ps.filename ? "Replace" : "+ Upload"}
                    <input type="file" className="hidden" disabled={docUploading === ps.id} onChange={(e) => { const f = e.target.files?.[0]; if (f) { void uploadPayslip(ps.id, f); e.target.value = ""; } }} />
                  </label>
                  <button onClick={() => removePayslip(ps.id)} className="text-neutral-300 hover:text-red-500 text-lg shrink-0">×</button>
                </div>
              ))}
              <button onClick={addPayslip} className="text-sm text-blue-600 hover:underline">+ Add payslip</button>
              <p className="mt-2 text-xs text-neutral-400">Phase 3: AI will read payslips to auto-populate income fields.</p>
            </div>
          </CollapsibleSection>

        </div>
      </div>}

    </div>
  );
}
